import React, { useMemo, useState } from 'react';
import { GATE_INFO, Rx, Ry, Rz } from './quantum/gates';
import { zeroState, basisState, applyGate, probabilities, blochVectorForQubit, measureQubit } from './quantum/QuantumState';
import { fmtNumber, fmtComplex } from './quantum/format';
import BlochSphereCanvas from './BlochSphereCanvas.jsx';

const ROTATION_ANGLE = Math.PI / 2; // 90°, kept fixed for a simple, predictable playground

const PLAYGROUND_GATES = [
  ...['X', 'Y', 'Z', 'H', 'S', 'T'].map((id) => ({
    id,
    symbol: id,
    name: GATE_INFO[id].name,
    description: GATE_INFO[id].description,
    matrix: GATE_INFO[id].matrix,
    color: { X: '#dc2626', Y: '#d97706', Z: '#7c3aed', H: '#0078D4', S: '#0d9488', T: '#db2777' }[id],
  })),
  { id: 'Rx90', symbol: 'Rx(90°)', name: 'Rotation about X (90°)', description: 'Rotates the Bloch vector 90° about the X axis.', matrix: Rx(ROTATION_ANGLE), color: '#dc2626' },
  { id: 'Ry90', symbol: 'Ry(90°)', name: 'Rotation about Y (90°)', description: 'Rotates the Bloch vector 90° about the Y axis.', matrix: Ry(ROTATION_ANGLE), color: '#d97706' },
  { id: 'Rz90', symbol: 'Rz(90°)', name: 'Rotation about Z (90°)', description: 'Rotates the Bloch vector 90° about the Z axis.', matrix: Rz(ROTATION_ANGLE), color: '#7c3aed' },
];

const INITIAL_STATES = {
  '0': { label: '|0⟩', state: zeroState(1) },
  '1': { label: '|1⟩', state: basisState(1, 1) },
  '+': { label: '|+⟩', state: applyGate(zeroState(1), GATE_INFO.H.matrix, 0, 1) },
  '-': { label: '|−⟩', state: applyGate(basisState(1, 1), GATE_INFO.H.matrix, 0, 1) },
};

const fmt = fmtNumber;

const stateLabel = (state) => `(${fmtComplex(state[0])})|0⟩ + (${fmtComplex(state[1])})|1⟩`;

export default function GatesPlayground() {
  const [initialId, setInitialId] = useState('0');
  const [history, setHistory] = useState([]); // array of gate ids from PLAYGROUND_GATES
  const [measured, setMeasured] = useState(null); // { result } or null

  const gateById = useMemo(() => Object.fromEntries(PLAYGROUND_GATES.map((g) => [g.id, g])), []);

  const statesAtEachStep = useMemo(() => {
    const steps = [INITIAL_STATES[initialId].state];
    for (const gateId of history) {
      const gate = gateById[gateId];
      steps.push(applyGate(steps[steps.length - 1], gate.matrix, 0, 1));
    }
    return steps;
  }, [initialId, history, gateById]);

  const currentState = statesAtEachStep[statesAtEachStep.length - 1];
  const probs = probabilities(currentState);
  const bloch = blochVectorForQubit(currentState, 1, 0);
  const lastGate = history.length > 0 ? gateById[history[history.length - 1]] : null;
  const stateBeforeLast = statesAtEachStep[statesAtEachStep.length - 2];

  const applyGateToPlayground = (gateId) => {
    setMeasured(null);
    setHistory((h) => [...h, gateId]);
  };

  const reset = () => {
    setHistory([]);
    setMeasured(null);
  };

  const undo = () => {
    setMeasured(null);
    setHistory((h) => h.slice(0, -1));
  };

  const handleMeasure = () => {
    const { result } = measureQubit(currentState, 1, 0);
    setMeasured({ result });
  };

  return (
    <div className="min-h-screen bg-gray-50 p-4">
      <div className="max-w-6xl mx-auto">
        <div className="text-center mb-6">
          <h1 className="text-4xl font-bold text-gray-900 mb-1">Quantum Gates Playground</h1>
          <p className="text-gray-600">
            Pick a starting state, apply gates one at a time, and watch the state vector and Bloch sphere update after every step.
          </p>
        </div>

        {/* Initial state selector */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm mb-4">
          <h2 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">1. Choose an initial state</h2>
          <div className="flex flex-wrap gap-2">
            {Object.entries(INITIAL_STATES).map(([id, { label }]) => (
              <button
                key={id}
                onClick={() => { setInitialId(id); reset(); }}
                className={`px-4 py-2 rounded-lg font-semibold text-sm transition-all ${
                  initialId === id ? 'bg-blue-600 text-white shadow-md' : 'bg-gray-50 text-gray-700 border border-gray-200 hover:border-blue-300'
                }`}
              >
                {label}
              </button>
            ))}
          </div>
        </div>

        {/* Gate palette */}
        <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm mb-4">
          <h2 className="text-sm font-bold text-gray-700 mb-2 uppercase tracking-wide">2. Apply gates</h2>
          <div className="flex flex-wrap gap-2 mb-3">
            {PLAYGROUND_GATES.map((gate) => (
              <button
                key={gate.id}
                onClick={() => applyGateToPlayground(gate.id)}
                title={gate.description}
                className="px-4 py-2 rounded-lg font-bold text-sm text-white shadow-sm transform hover:scale-105 transition-all"
                style={{ background: gate.color }}
              >
                {gate.symbol}
              </button>
            ))}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <span className="text-xs text-gray-500 font-semibold">Sequence:</span>
            <span className="font-mono text-sm text-gray-800">
              {INITIAL_STATES[initialId].label}
              {history.map((id) => ` → ${gateById[id].symbol}`)}
            </span>
            {history.length > 0 && (
              <>
                <button onClick={undo} className="ml-2 px-3 py-1 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold">
                  ↩ Undo last
                </button>
                <button onClick={reset} className="px-3 py-1 text-xs rounded-lg bg-gray-100 text-gray-700 hover:bg-gray-200 font-semibold">
                  ↻ Reset
                </button>
              </>
            )}
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {/* Last gate detail */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h2 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide">Last operation</h2>
            {lastGate ? (
              <div className="space-y-3">
                <div className="flex items-center gap-3">
                  <span className="w-10 h-10 rounded-lg flex items-center justify-center text-white font-bold" style={{ background: lastGate.color }}>
                    {lastGate.symbol.replace(/\(.+\)/, '')}
                  </span>
                  <div>
                    <div className="font-bold text-gray-900">{lastGate.name}</div>
                    <div className="text-xs text-gray-500">{lastGate.description}</div>
                  </div>
                </div>
                <div className="font-mono text-xs text-gray-600 bg-gray-50 rounded-lg p-3 border border-gray-100">
                  Matrix: [[{fmtComplex(lastGate.matrix[0][0])}, {fmtComplex(lastGate.matrix[0][1])}], [{fmtComplex(lastGate.matrix[1][0])}, {fmtComplex(lastGate.matrix[1][1])}]]
                </div>
                <div className="text-sm">
                  <div className="text-gray-500 text-xs mb-0.5">State before</div>
                  <div className="font-mono text-gray-800">{stateLabel(stateBeforeLast)}</div>
                </div>
                <div className="text-sm">
                  <div className="text-gray-500 text-xs mb-0.5">State after</div>
                  <div className="font-mono text-blue-700 font-semibold">{stateLabel(currentState)}</div>
                </div>
              </div>
            ) : (
              <p className="text-gray-500 text-sm">Apply a gate above to see its matrix, description, and effect on the state.</p>
            )}
          </div>

          {/* Bloch sphere + probabilities */}
          <div className="bg-white rounded-xl p-4 border border-gray-200 shadow-sm">
            <h2 className="text-sm font-bold text-gray-700 mb-3 uppercase tracking-wide text-center">Current State</h2>
            <div className="flex justify-center mb-3">
              <BlochSphereCanvas vector={bloch} />
            </div>
            <div className="text-center font-mono text-gray-800 mb-3">{stateLabel(currentState)}</div>
            <div className="grid grid-cols-2 gap-2 text-xs text-gray-500 text-center mb-3">
              <div>Bloch: x={fmt(bloch.x)}, y={fmt(bloch.y)}, z={fmt(bloch.z)}</div>
              <div>|α|² + |β|² = {fmt(probs[0] + probs[1])}</div>
            </div>
            <div className="grid grid-cols-2 gap-2 mb-3">
              <div className="bg-cyan-50 rounded-lg p-2 relative border border-cyan-100">
                <div className="text-cyan-600 text-xs">P(|0⟩)</div>
                <div className="text-cyan-700 text-base font-bold">{(probs[0] * 100).toFixed(1)}%</div>
              </div>
              <div className="bg-pink-50 rounded-lg p-2 relative border border-pink-100">
                <div className="text-pink-600 text-xs">P(|1⟩)</div>
                <div className="text-pink-700 text-base font-bold">{(probs[1] * 100).toFixed(1)}%</div>
              </div>
            </div>
            <button
              onClick={handleMeasure}
              className="w-full px-4 py-2 bg-purple-600 hover:bg-purple-700 text-white font-bold text-sm rounded-lg shadow-sm transition-all"
            >
              🔍 Measure
            </button>
            {measured && (
              <div className="mt-2 text-center text-sm">
                Collapsed to <span className="font-bold text-purple-700">|{measured.result}⟩</span>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
